import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, btnP, sp, rad, bdg } from "../../constants/styles";
import { api, API_URL } from "../../api";
import FitBadge from "./FitBadge";
import Ic from "./Ic";
import { getPropertyFit } from "../../utils/propertyFit";
import { formatPropAddr } from "../../utils/propertyAddr";
import useLocationFilter from "../../hooks/useLocationFilter";
import LocationBar from "./LocationBar";
import { withDistances, sortByDistance, filterByRadius, formatDistance } from "../../utils/geo";
import { COM_TYPES, isCommercial, ptLabel, ptShort } from "../../utils/propertyTypes";
import { RatingSummary, LandlordReviews } from "./Reviews";

const SIVAKASI = [9.4533, 77.8024];
const TILE_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>';

function MapViewPage({ sp: spData }) {
  const { t, h, props, mts, tens, go, selPropId, setSelPropId, role, leases, flash } = useContext(Ctx);
  const [view, setView] = useState("map"); // "map" or "list"
  const [selected, setSelected] = useState(null);
  // Tenants always see only vacant — no toggle allowed
  const [vacantOnly, setVacantOnly] = useState(role === "tenant" || role === "user" ? true : false);
  const [showFavOnly, setShowFavOnly] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [detailProp, setDetailProp] = useState(null);
  const [inqName, setInqName] = useState("");
  const [inqPhone, setInqPhone] = useState("");
  const [inqMsg, setInqMsg] = useState("");
  const [inqSending, setInqSending] = useState(false);
  const [inqSent, setInqSent] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [leafletReady, setLeafletReady] = useState(false);
  const [publicProps, setPublicProps] = useState([]);
  const [loadingPublic, setLoadingPublic] = useState(false);
  // Browse filters (tenant/user)
  const [prefRooms, setPrefRooms] = useState(0);
  const [prefMaxRent, setPrefMaxRent] = useState("");
  const [prefFurnishing, setPrefFurnishing] = useState("any");
  const [prefPropType, setPrefPropType] = useState("any");
  const [prefCommType, setPrefCommType] = useState("any");
  const mapRef = useRef(null);
  const leafletModules = useRef(null);

  // Location bar — "📍 Use my location" + "🔍 Search a place" +
  // radius chips. Same shared hook used by PublicBrowsePage. Storage
  // key is namespaced so the tenant Map View doesn't leak its
  // active location into the public browse experience.
  const locFilter = useLocationFilter({ storageKey: 'mapViewLocation' });

  // Fullscreen map toggle — expands the map container to fill the
  // entire viewport (position:fixed inset:0). All floating controls
  // (📍 recenter, exit-fullscreen, preview card) remain operable in
  // fullscreen mode. Restoring just flips the bool back. We keep
  // this as React state instead of using the browser Fullscreen API
  // (a) to preserve our app's bottom-nav-aware safe areas and
  // (b) because the FS API requires a user gesture per call which
  // breaks the auto-resize-on-orientation-change use case.
  const [fullscreenMap, setFullscreenMap] = useState(false);

  // For tenants: always fetch public properties so they can browse marketplace
  useEffect(() => {
    if (role === "tenant" || role === "user") {
      setLoadingPublic(true);
      // Use the shared API base — re-deriving from window.location breaks
      // the Android APK (Capacitor serves from https://localhost, which
      // resolved this to a nonexistent http://localhost:5000).
      // NB: API_URL already ends in /api — don't prefix the path with it
      // again. The double "/api/api/..." 404s as JSON ({error:"Not
      // found"}), which silently left the tenant marketplace map empty.
      fetch(`${API_URL}/public/properties`)
        .then(r => r.json())
        .then(d => { if (d.properties) setPublicProps(d.properties); })
        .catch(e => console.error("Public properties fetch error:", e))
        .finally(() => setLoadingPublic(false));
    }
    // Load favorites for logged-in users
    api.getFavorites().then(r => { if (r?.favorites) setFavorites(Array.isArray(r.favorites) ? r.favorites.map(f => typeof f === 'string' ? f : f.property_id) : []); }).catch(() => {});
  }, [role]);

  const isFav = (id) => favorites.includes(id);
  const toggleFav = async (id, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const was = favorites.includes(id);
    const newFavs = was ? favorites.filter(x => x !== id) : [...favorites, id];
    setFavorites(newFavs);
    // If unfavoriting while in favorites filter and no more favs, turn off filter
    if (was && showFavOnly && newFavs.length === 0) setShowFavOnly(false);
    flash(was ? "Removed from saved" : "Saved!");
    const res = await api.toggleFavorite(id);
    if (!res?.success) setFavorites(was ? [...newFavs, id] : newFavs.filter(x => x !== id));
  };

  // Tenants/public see marketplace; service see only job properties; landlords see all theirs
  const allProps = (role === "tenant" || role === "user")
    ? publicProps
    : role === "service"
      ? (() => {
          // Only show properties where this SP has active (non-completed) jobs
          const spJobs = spData?.data?.jobs || [];
          const spTens = spData?.data?.tens || [];
          const spProps = spData?.data?.props || [];
          const activeJobPropIds = new Set(
            spJobs
              .filter(j => j.sp_status !== "completed" && !j.confirmed_by_tenant)
              .map(j => { const te = spTens.find(x => x.id === j.tid); return te?.pid; })
              .filter(Boolean)
          );
          // No fallback — once jobs are done, no pins shown
          return spProps.filter(p => activeJobPropIds.has(p.id));
        })()
      : props;

  const isBrowseRole = role === "tenant" || role === "user";

  // Deep-link: when a browse-role user arrives with selPropId set (e.g. tapping
  // a card in "My Inquiries"), open that property's detail directly, then clear
  // the signal so a normal Browse Properties visit still lands on the map.
  // Search allProps — for tenants that's the fetched marketplace list
  // (publicProps), not the (empty) context props.
  useEffect(() => {
    if (!selPropId || !isBrowseRole || !allProps || allProps.length === 0) return;
    const found = allProps.find(p => p.id === selPropId);
    if (found) { setDetailProp(found); setInqSent(false); }
    else { flash(t.listingUnavailable || "This listing is no longer available", "error"); }
    setSelPropId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selPropId, allProps, isBrowseRole]);

  // Filter properties that have coordinates + apply browse filters +
  // optional distance sort/filter when the user has set a location.
  // Location work happens LAST so the preference filters (rooms / rent
  // / type) still apply to the radius-filtered subset, not the whole
  // catalog. That gives "2BHK within 3km of Sivakasi" semantics.
  const geoProps = useMemo(() => {
    let filtered = allProps.filter(p => p.latitude && p.longitude);
    if (vacantOnly) filtered = filtered.filter(p => !p.occ);
    if (showFavOnly) filtered = filtered.filter(p => favorites.includes(p.id));
    if (isBrowseRole) {
      if (prefRooms > 0) filtered = filtered.filter(p => p.rooms === prefRooms);
      if (prefMaxRent) filtered = filtered.filter(p => p.rent <= parseInt(prefMaxRent));
      if (prefFurnishing !== "any") filtered = filtered.filter(p => (p.furnishing || "").toLowerCase() === prefFurnishing);
      if (prefPropType !== "any") filtered = filtered.filter(p => p.property_type === prefPropType);
      if (prefCommType !== "any") filtered = filtered.filter(p => p.property_type === prefCommType);
    }
    if (locFilter.userLocation) {
      filtered = withDistances(filtered, locFilter.userLocation);
      filtered = filterByRadius(filtered, locFilter.radiusKm);
      filtered = sortByDistance(filtered);
    }
    return filtered;
  }, [allProps, vacantOnly, showFavOnly, favorites, isBrowseRole, prefRooms, prefMaxRent, prefFurnishing, prefPropType, prefCommType, locFilter.userLocation, locFilter.radiusKm]);

  // Auto-pan the map when the user picks a new reference point while
  // viewing the map. Leaflet ignores the `center` prop after first
  // mount, so we push setView() imperatively. Skipped on list view.
  useEffect(() => {
    if (view !== "map") return;
    if (!locFilter.userLocation || !mapRef.current?.setView) return;
    try {
      mapRef.current.setView([locFilter.userLocation.lat, locFilter.userLocation.lng], 14);
    } catch (_) { /* map not yet ready */ }
  }, [locFilter.userLocation, view]);

  // When the map container resizes (entering/exiting fullscreen),
  // Leaflet needs invalidateSize() to recompute or it renders a tile
  // subset that doesn't match the new container bounds. Delay one
  // tick so the DOM reflow happens BEFORE Leaflet measures.
  useEffect(() => {
    if (view !== "map" || !mapRef.current?.invalidateSize) return;
    const id = setTimeout(() => {
      try { mapRef.current.invalidateSize(); } catch (_) {}
    }, 50);
    return () => clearTimeout(id);
  }, [fullscreenMap, view]);

  // Dynamically load leaflet + react-leaflet to avoid SSR / global CSS issues
  useEffect(() => {
    let cancelled = false;
    const loadLeaflet = async () => {
      // Inject Leaflet CSS
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }
      try {
        const [L, RL] = await Promise.all([
          import("leaflet"),
          import("react-leaflet")
        ]);
        if (!cancelled) {
          leafletModules.current = { L: L.default || L, ...RL };
          setLeafletReady(true);
        }
      } catch (e) {
        console.error("Leaflet load error", e);
      }
    };
    loadLeaflet();
    return () => { cancelled = true; };
  }, []);

  // Global callback for marker clicks — bulletproof, works even when Leaflet eventHandlers fail
  const geoPropRef = useRef([]);
  geoPropRef.current = geoProps;
  useEffect(() => {
    window.__mapPinClick = (propId) => {
      const p = geoPropRef.current.find(x => x.id === propId);
      // Mirror handleMarkerClick exactly: the inline divIcon onclick
      // stopPropagation()s, so Leaflet's own click handler (which
      // enters fullscreen) never fires on touch devices. Without the
      // fullscreen hop the preview card renders inside the small
      // embedded map, whose bottom edge sits under the fixed bottom
      // nav — hiding the "View Details" row.
      if (p) { setSelected(p); setFullscreenMap(true); }
    };
    return () => { delete window.__mapPinClick; };
  }, []);

  // Tapping a pin auto-enters fullscreen for a focused preview —
  // the map fills the viewport, the user sees the property bubble
  // pin highlighted, and the bottom-sheet preview card lands without
  // competing with surrounding chrome (header, filters, bottom nav).
  // Closing the preview (× button) leaves them in fullscreen so they
  // can keep browsing pins. Tapping "View Details" exits fullscreen
  // and routes to the detail view.
  const handleMarkerClick = (p) => {
    setSelected(p);
    setFullscreenMap(true);
  };
  const handleView = (p) => {
    if (role === "landlord") { setSelPropId(p.id); go("editProp"); }
    // service: tapping "view" on a pin just dismisses the popup (map shows job location)
  };

  // Empty state
  if (geoProps.length === 0) {
    // Tenants/users: show loading while fetching public props
    if (isBrowseRole && loadingPublic) {
      return <div style={{ paddingTop: 40, textAlign: "center" }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
        <p style={{ color: h.sc, fontSize: 13, marginTop: 12 }}>Loading properties...</p>
      </div>;
    }
    // Tenants/users: fetched but no geo props
    if (isBrowseRole && !loadingPublic && publicProps.length > 0) {
      // Props exist but none have coordinates — show list view instead
    } else if (!isBrowseRole) {
      return <div style={{ paddingTop: 16 }}>
        <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
        <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{t.mapView}</h2>
        <div style={{ ...crd(h), textAlign: "center", padding: 32 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{"\uD83D\uDDFA"}</div>
          <div style={{ color: h.tx, fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t.noLocData}</div>
          <div style={{ color: h.sc, fontSize: 12 }}>{t.addLocHint}</div>
        </div>
      </div>;
    }
  }

  // Create marker icon with inline onclick for bulletproof click handling
  // Leaflet marker pane has pointer-events:none; inline onclick + pointer-events:auto ensures clicks work
  const makeIcon = (occ, rent, propId, isServicePin = false, propertyType = null) => {
    if (!leafletModules.current) return null;
    const L = leafletModules.current.L;
    const clk = `onclick="event.stopPropagation();window.__mapPinClick&&window.__mapPinClick('${propId}')"`;
    const pe = "pointer-events:auto;cursor:pointer";
    // Service person: orange pin with wrench
    if (isServicePin) {
      return L.divIcon({ className: "", iconSize: [36, 36], iconAnchor: [18, 36],
        html: `<div ${clk} style="${pe};width:36px;height:36px;border-radius:50% 50% 50% 0;background:#f59e0b;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,.35);border:2.5px solid #fff;display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);font-size:14px;line-height:1">\uD83D\uDD27</span></div>` });
    }
    // Vacant with price: GREEN (residential) or PURPLE (commercial) bubble
    if (!occ && rent) {
      const priceLabel = rent >= 1000 ? `${Math.round(rent / 1000)}K` : `${rent}`;
      const isComm = isCommercial(propertyType);
      const bgColor = isComm ? "#8b5cf6" : "#22c55e";
      const typeLabel = isComm ? (ptShort(propertyType) || "Commercial") : "";
      const label = typeLabel ? `${typeLabel} \u00B7 \u20B9${priceLabel}` : `\u20B9${priceLabel}`;
      const width = isComm ? 100 : 52;
      return L.divIcon({ className: "", iconSize: [width, 28], iconAnchor: [width / 2, 28],
        html: `<div ${clk} style="${pe};background:${bgColor};color:#fff;font-size:10px;font-weight:700;padding:4px 8px;border-radius:12px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.3);border:2px solid #fff;text-align:center">${label}</div>` });
    }
    // Vacant without price: ORANGE bubble
    if (!occ) {
      return L.divIcon({ className: "", iconSize: [48, 28], iconAnchor: [24, 28],
        html: `<div ${clk} style="${pe};background:#f59e0b;color:#fff;font-size:10px;font-weight:700;padding:4px 8px;border-radius:12px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.3);border:2px solid #fff;text-align:center">Vacant</div>` });
    }
    // Occupied: small gray dot
    return L.divIcon({ className: "", iconSize: [14, 14], iconAnchor: [7, 7],
      html: `<div ${clk} style="${pe};width:14px;height:14px;border-radius:50%;background:#8e8e93;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.2);opacity:0.4"></div>` });
  };

  return <div style={{ paddingTop: 16 }}>
    {/* Header */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13 }}>&larr; {t.back}</button>
      <h2 style={{ color: h.tx, fontSize: 16, fontWeight: 700, margin: 0 }}>{t.mapView}</h2>
      <button onClick={() => { setView(view === "map" ? "list" : "map"); setSelected(null); }} style={{ background: `${h.ac}15`, border: `1px solid ${h.ac}`, color: h.ac, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
        {view === "map" ? t.listView : t.mapView}
      </button>
    </div>

    {/* Filter chips — hide vacant/saved filters for service role */}
    <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
      {/* Landlord: show All / Vacant toggle. Tenant/user: always vacant, no toggle shown */}
      {role === "landlord" && <button onClick={() => setVacantOnly(false)} style={{ padding: "5px 12px", borderRadius: 16, border: `1px solid ${!vacantOnly ? h.ac : h.bd}`, background: !vacantOnly ? `${h.ac}15` : "transparent", color: !vacantOnly ? h.ac : h.sc, cursor: "pointer", fontSize: 11, fontWeight: !vacantOnly ? 600 : 400 }}>{t.allProps}</button>}
      {role === "landlord" && <button onClick={() => setVacantOnly(true)} style={{ padding: "5px 12px", borderRadius: 16, border: `1px solid ${vacantOnly ? h.wn : h.bd}`, background: vacantOnly ? `${h.wn}15` : "transparent", color: vacantOnly ? h.wn : h.sc, cursor: "pointer", fontSize: 11, fontWeight: vacantOnly ? 600 : 400 }}>{t.vacantOnly}</button>}
      {role === "service" && <div style={{ padding: "5px 12px", borderRadius: 16, background: `${h.wn}15`, border: `1px solid ${h.wn}30`, color: h.wn, fontSize: 11, fontWeight: 600 }}>🔧 {t.myJobs || "My Job Locations"}</div>}
      {isBrowseRole && favorites.length > 0 && <button onClick={() => setShowFavOnly(!showFavOnly)} style={{ padding: "5px 12px", borderRadius: 16, border: `1px solid ${showFavOnly ? "#ff375f" : h.bd}`, background: showFavOnly ? "rgba(255,55,95,0.1)" : "transparent", color: showFavOnly ? "#ff375f" : h.sc, cursor: "pointer", fontSize: 11, fontWeight: showFavOnly ? 600 : 400 }}>{"\u2665"} {t.favorites || "Favorites"} ({favorites.length})</button>}
      {/* Quick access to the renter's own sent inquiries while browsing. */}
      {role === "tenant" && <button onClick={() => go("tenInquiries")} style={{ padding: "5px 12px", borderRadius: 16, border: `1px solid ${h.ac}`, background: `${h.ac}10`, color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{"\ud83d\udce9 "}{t.myInquiries || "My Inquiries"}</button>}
    </div>

    {/* Your Preferences — browse filters for tenant/user */}
    {isBrowseRole && <div style={{ ...crd(h), marginBottom: 12, padding: 12 }}>
      <div style={{ color: h.tx, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{t.yourPreferences || "Your Preferences"}</div>
      {/* Row 1: Rooms */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ color: h.sc, fontSize: 10, marginBottom: 4 }}>{t.rooms || "Rooms"}</div>
        <div style={{ display: "flex", gap: 4 }}>
          {[0, 1, 2, 3, 4, 5].map(r => <button key={r} onClick={() => setPrefRooms(r === prefRooms ? 0 : r)} style={{
            padding: "5px 12px", borderRadius: 12, cursor: "pointer", fontSize: 11, fontWeight: prefRooms === r ? 600 : 400,
            border: `1px solid ${prefRooms === r ? h.ac : h.bd}`,
            background: prefRooms === r ? `${h.ac}15` : "transparent",
            color: prefRooms === r ? h.ac : h.sc
          }}>{r === 0 ? (t.any || "Any") : r}</button>)}
        </div>
      </div>
      {/* Row 2: Max Budget + Furnishing */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ color: h.sc, fontSize: 10, marginBottom: 4 }}>{t.maxBudget || "Max Budget"}</div>
          <input value={prefMaxRent} onChange={e => setPrefMaxRent(e.target.value.replace(/\D/g, ''))}
            placeholder={"\u20B9 Max"} type="text" style={{ width: "100%", padding: "6px 10px", borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 11, outline: "none", boxSizing: "border-box" }} />
        </div>
        <div>
          <div style={{ color: h.sc, fontSize: 10, marginBottom: 4 }}>{t.furnishing || "Furnishing"}</div>
          <select value={prefFurnishing} onChange={e => setPrefFurnishing(e.target.value)}
            style={{ width: "100%", padding: "6px 8px", borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 11, outline: "none", boxSizing: "border-box" }}>
            <option value="any">{t.any || "Any"}</option>
            <option value="furnished">{t.furnished || "Furnished"}</option>
            <option value="semi-furnished">{t.semiFurnished || "Semi"}</option>
            <option value="unfurnished">{t.unfurnished || "Unfurnished"}</option>
          </select>
        </div>
      </div>
      {/* Row 3: Property Type + Commercial Type */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <div style={{ color: h.sc, fontSize: 10, marginBottom: 2 }}>{t.propertyType || "Property Type"}</div>
          <select value={prefPropType} onChange={e => { setPrefPropType(e.target.value); if (e.target.value !== "any") setPrefCommType("any"); }}
            style={{ width: "100%", padding: "4px 6px", borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 10, outline: "none", boxSizing: "border-box" }}>
            <option value="any">{t.any || "Any"}</option>
            <option value="apartment">{t.ptApartment || "Apartment"}</option>
            <option value="independent_house">{t.ptIndependentHouse || "Independent House"}</option>
            <option value="villa">{t.ptVilla || "Villa"}</option>
            <option value="pg_room">{t.ptPgRoom || "PG Room"}</option>
          </select>
        </div>
        <div>
          <div style={{ color: h.sc, fontSize: 10, marginBottom: 2 }}>{t.commercialType || "Commercial Type"}</div>
          <select value={prefCommType} onChange={e => { setPrefCommType(e.target.value); if (e.target.value !== "any") setPrefPropType("any"); }}
            style={{ width: "100%", padding: "4px 6px", borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 10, outline: "none", boxSizing: "border-box" }}>
            <option value="any">{t.any || "Any"}</option>
            {COM_TYPES.map(ct => <option key={ct} value={ct}>{ptLabel(ct, t)}</option>)}
          </select>
        </div>
      </div>
    </div>}

    {/* Location bar \u2014 "\ud83d\udccd Use my location" + "\ud83d\udd0d Search a place" +
        radius chips. Works the same in both map view (auto-pans the
        map) and list view (sorts cards nearest-first). Available to
        all roles; primarily useful for tenants browsing the
        marketplace, but landlords can use it too when looking at
        their own portfolio map. */}
    {/* Bar buttons duplicate the map's floating 📍/🔍 buttons when
        view === 'map', so hide them in that case. In list view the
        map isn't visible — the bar must surface both buttons or
        there's no way to set a location at all. */}
    <LocationBar
      filter={locFilter} h={h} t={t}
      hideSearchButton={view === "map"}
      hideLocationButton={view === "map"}
    />


    {view === "map" ? (
      /* Map View — measured chrome on mobile (375×812):
           top bar + page header + view toggle + filter card ≈ 336px above
           bottom nav (incl. safe-area-inset)              ≈ 70px below
         So 410px total subtraction keeps the map clear of the bottom bar
         on the smallest common viewport. minHeight floor for short
         landscape / split-screen cases.
         When fullscreenMap is true, the container takes over the whole
         viewport via position:fixed inset:0. The floating controls
         (📍, ⤡ exit-fullscreen, preview card) remain absolutely
         positioned inside, so they work identically in both modes. */
      <div style={fullscreenMap ? {
        // No explicit 100vh height — on mobile browsers 100vh includes
        // the area behind the collapsing URL/system bars, pushing the
        // bottom card off-screen. inset:0 alone pins all four edges to
        // the actual visible viewport.
        position: "fixed", inset: 0, zIndex: 99998,
        borderRadius: 0, border: "none",
        overflow: "hidden", marginBottom: 0, background: h.bg,
      } : {
        // zIndex: 0 makes this wrapper a stacking context, trapping
        // Leaflet's internal z-indexes (panes 400-700, controls 1000)
        // inside the card — otherwise they paint OVER the fixed bottom
        // nav (zIndex 50). Fullscreen mode above intentionally covers it.
        position: "relative", zIndex: 0, borderRadius: 14, overflow: "hidden",
        height: "calc(100vh - 410px - env(safe-area-inset-bottom, 0px))",
        minHeight: 300, border: `1px solid ${h.bd}`, marginBottom: 16,
      }}>
        {leafletReady && leafletModules.current ? (() => {
          const { MapContainer, TileLayer, Marker, useMap } = leafletModules.current;

          // Auto-fit component
          const FitBounds = ({ points }) => {
            const map = useMap();
            useEffect(() => {
              if (points.length > 0) {
                const L = leafletModules.current.L;
                const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
                map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
              }
            }, [points, map]);
            return null;
          };

          // "You are here" pulsing-blue pin — only when the user has
          // granted GPS or picked a search location. Visually distinct
          // from property pins (green/purple price bubbles) so the user
          // can immediately spot themselves on the map.
          const userIcon = locFilter.userLocation && leafletModules.current.L.divIcon({
            className: "",
            iconSize: [22, 22],
            iconAnchor: [11, 11],
            html: `<div style="position:relative;width:22px;height:22px;">
              <div style="position:absolute;inset:-6px;border-radius:50%;background:#0a84ff44;animation:geo-ping 1.8s cubic-bezier(0,0,.2,1) infinite"></div>
              <div style="position:absolute;inset:0;border-radius:50%;background:#0a84ff;border:3px solid #fff;box-shadow:0 2px 8px rgba(10,132,255,.5)"></div>
            </div>`,
          });
          return <MapContainer
            ref={mapRef}
            center={locFilter.userLocation ? [locFilter.userLocation.lat, locFilter.userLocation.lng] : (geoProps.length > 0 ? [geoProps[0].latitude, geoProps[0].longitude] : SIVAKASI)}
            zoom={13}
            style={{ height: "100%", width: "100%" }}
            zoomControl={false}
          >
            <TileLayer url={TILE_URL} attribution={TILE_ATTR} />
            {geoProps.length > 1 && <FitBounds points={geoProps} />}
            {geoProps.map(p => <Marker
              key={p.id}
              position={[p.latitude, p.longitude]}
              icon={makeIcon(p.occ, p.rent, p.id, role === "service", p.property_type)}
              eventHandlers={{ click: () => handleMarkerClick(p) }}
            />)}
            {locFilter.userLocation && userIcon && <Marker
              position={[locFilter.userLocation.lat, locFilter.userLocation.lng]}
              icon={userIcon}
            />}
          </MapContainer>;
        })() : <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: h.bg }}>
          <div style={{ color: h.sc, fontSize: 13 }}>Loading map...</div>
        </div>}

        {/* Floating ⛶ / ⤡ fullscreen toggle — top-LEFT so it doesn't
            clash with the 📍 recenter button (top-right). Visible
            regardless of preview state, since exiting fullscreen
            should always be one tap away — even if the user has the
            preview card open. */}
        <button
          onClick={() => setFullscreenMap(v => !v)}
          style={{
            position: "absolute",
            top: 12, left: 12,
            zIndex: 1001,
            width: 44, height: 44,
            borderRadius: "50%",
            border: "none",
            background: fullscreenMap ? "#0a84ff" : h.cd,
            color: fullscreenMap ? "#fff" : h.ac,
            cursor: "pointer",
            boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s",
          }}
          aria-label={fullscreenMap ? "Exit fullscreen" : "Enter fullscreen"}
          title={fullscreenMap ? "Exit fullscreen" : "Enter fullscreen"}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {fullscreenMap ? (
              // "exit fullscreen" — four arrows pointing inward
              <>
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </>
            ) : (
              // "enter fullscreen" — four arrows pointing outward
              <>
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </>
            )}
          </svg>
        </button>

        {/* Floating 🔍 search lens — sits LEFT of the 📍 button so
            both top-right controls form a horizontal pair. Tap →
            fullscreen search modal (rendered by LocationBar). Hidden
            when the preview card is open, same as 📍. */}
        {!selected && (
          <button
            onClick={() => locFilter.setSearchOpen(true)}
            style={{
              position: "absolute",
              top: 12, right: 64,
              zIndex: 1000,
              width: 44, height: 44,
              borderRadius: "50%",
              border: "none",
              background: h.cd,
              color: h.ac,
              cursor: "pointer",
              boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}
            aria-label="Search a place"
            title="Search a place"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        )}

        {/* Floating 📍 button — first tap requests permission + pans;
            subsequent taps recenter on the saved location. Auto-hides
            while the preview card is open so it doesn't clash. Same
            pattern as PublicBrowsePage's map. */}
        {!selected && (
          <button
            onClick={async () => {
              if (!locFilter.userLocation) {
                await locFilter.requestLocation();
                try {
                  const fresh = JSON.parse(sessionStorage.getItem('mapViewLocation') || 'null');
                  if (fresh && mapRef.current?.setView) {
                    mapRef.current.setView([fresh.lat, fresh.lng], 14);
                  }
                } catch (_) {}
              } else if (mapRef.current?.setView) {
                mapRef.current.setView([locFilter.userLocation.lat, locFilter.userLocation.lng], 14);
              }
            }}
            disabled={locFilter.geolocating}
            style={{
              position: "absolute",
              top: 12, right: 12,
              zIndex: 1000,
              width: 44, height: 44,
              borderRadius: "50%",
              border: "none",
              background: locFilter.userLocation ? "#0a84ff" : h.cd,
              color: locFilter.userLocation ? "#fff" : h.ac,
              cursor: locFilter.geolocating ? "wait" : "pointer",
              boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
              fontSize: 18,
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: locFilter.geolocating ? 0.7 : 1,
              transition: "all 0.2s",
            }}
            aria-label={locFilter.userLocation ? "Recenter on my location" : "Use my location"}
            title={locFilter.userLocation ? "Recenter on my location" : "Use my location"}
          >
            {locFilter.geolocating ? "…" : "📍"}
          </button>
        )}

        {/* Preview card */}
        {selected && <div style={{
          position: "absolute", left: 12, right: 12,
          // Always float the ENTIRE card above the bottom-menu-bar zone
          // (~64px nav + margin), in fullscreen too — so "View Details"
          // can never hide under the app nav, the browser's collapsing
          // chrome, or a phone gesture bar.
          bottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
          background: h.cd, borderRadius: 12, padding: 14, boxShadow: "0 4px 20px rgba(0,0,0,.15)", zIndex: 1000, border: `1px solid ${h.bd}`,
        }}>
          <div style={{ position: "absolute", top: 8, right: 10, display: "flex", gap: 8 }}>
            {isBrowseRole && <button onClick={(e) => toggleFav(selected.id, e)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1 }}>
              {isFav(selected.id) ? <span style={{ color: "#ff375f" }}>{"\u2665"}</span> : <span style={{ color: h.sc }}>{"\u2661"}</span>}
            </button>}
            <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: h.sc, fontSize: 16, cursor: "pointer" }}>{"\u2715"}</button>
          </div>
          {/* Show owning org as a trust label, matching the PublicBrowsePage
              property cards. selected.org_name comes from
              /public/properties (joined on organizations). */}
          {selected.org_name && (
            <div style={{ color: h.ac, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2, paddingRight: 50 }}>
              {(t.listedBy || "Listed by")} {selected.org_name}
            </div>
          )}
          {Number(selected.ll_count) > 0 && <div style={{ marginBottom: 4 }}><RatingSummary avg={Number(selected.ll_avg) || 0} count={Number(selected.ll_count) || 0} h={h} size={12} /></div>}
          <div style={{ color: h.tx, fontWeight: 600, fontSize: 13, marginBottom: 4, paddingRight: 50 }}>{formatPropAddr(selected)}</div>
          {role === "service" && (() => {
            const spJobs = spData?.data?.jobs || []; const spTens = spData?.data?.tens || [];
            const relJob = spJobs.find(j => { const te = spTens.find(x => x.id === j.tid); return te?.pid === selected.id && j.sp_status !== "completed" && !j.confirmed_by_tenant; });
            return relJob ? <div style={{ background: `${h.wn}12`, border: `1px solid ${h.wn}25`, borderRadius: 8, padding: "6px 10px", marginBottom: 8 }}>
              <div style={{ color: h.wn, fontSize: 11, fontWeight: 600 }}>🔧 {relJob.desc}</div>
              {relJob.sp_status && <div style={{ color: h.sc, fontSize: 10, marginTop: 2, textTransform: "capitalize" }}>Status: {relJob.sp_status}</div>}
            </div> : null;
          })()}
          {role !== "service" && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
            {selected.property_type && selected.property_type !== "apartment" && <span style={{ background: isCommercial(selected.property_type) ? `${h.gn}15` : `${h.ac}15`, color: isCommercial(selected.property_type) ? h.gn : h.ac, padding: "1px 7px", borderRadius: 8, fontSize: 9, fontWeight: 600 }}>{ptLabel(selected.property_type, t)}</span>}
            {role !== "landlord" && <FitBadge fit={getPropertyFit(selected, { leases, role, t, h })} h={h} />}
            <span style={{ color: h.sc, fontSize: 11 }}>{selected.rooms} {t.rooms}</span>
            <span style={{ color: h.sc, fontSize: 11 }}>·</span>
            <span style={{ color: h.tx, fontSize: 12, fontWeight: 600 }}>₹{selected.rent?.toLocaleString()}/mo</span>
          </div>}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ padding: "3px 10px", borderRadius: 10, background: selected.occ ? `${h.gn}15` : `${h.wn}15`, color: selected.occ ? h.gn : h.wn, fontSize: 10, fontWeight: 600 }}>{selected.occ ? t.occupied : t.vacant}</span>
            {role === "landlord" && <button onClick={() => handleView(selected)} style={{ background: h.ac, color: "#fff", border: "none", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.edit + " \u2192"}</button>}
            {isBrowseRole && <button onClick={() => { setDetailProp(selected); setSelected(null); setFullscreenMap(false); }} style={{ background: h.ac, color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              {t.viewDetails || "View Details"}
            </button>}
            {role === "service" && selected.latitude && selected.longitude && (
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${selected.latitude},${selected.longitude}`} target="_blank" rel="noreferrer" style={{ background: "#10b981", color: "#fff", border: "none", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, textDecoration: "none", display: "flex", alignItems: "center", gap: 5 }}>
                🗺️ Directions
              </a>
            )}
          </div>
        </div>}
      </div>
    ) : (
      /* List View */
      <div>
        {geoProps.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: 20 }}>
          <div style={{ color: h.sc, fontSize: 12 }}>{vacantOnly ? t.noLocData : t.noLocData}</div>
        </div>}
        {geoProps.map(p => { const pPhotos = p.photos || []; return <div key={p.id} onClick={() => { if (role === "landlord") { setSelPropId(p.id); go("editProp"); } }} style={{ ...crd(h), cursor: role === "landlord" ? "pointer" : "default", overflow: "hidden", padding: 0 }}>
          {pPhotos.length > 0 && <div style={{ position: "relative" }}>
            <img src={pPhotos[0]} alt="" style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />
            {isBrowseRole && <button onClick={(e) => toggleFav(p.id, e)} style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.3)", border: "none", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: isFav(p.id) ? "#ff375f" : "#fff", fontSize: 16 }}>{isFav(p.id) ? "\u2665" : "\u2661"}</span>
            </button>}
          </div>}
          <div style={{ padding: pPhotos.length > 0 ? "10px 14px 14px" : 14, position: "relative" }}>
          {!pPhotos.length && isBrowseRole && <button onClick={(e) => toggleFav(p.id, e)} style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: 0 }}>
            {isFav(p.id) ? <span style={{ color: "#ff375f" }}>{"\u2665"}</span> : <span style={{ color: h.sc }}>{"\u2661"}</span>}
          </button>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
              {/* "Listed by <Org>" trust label, same treatment as the
                  PublicBrowsePage cards + map popup + detail screen.
                  Falls back gracefully when org_name isn't on the
                  property record (e.g. landlord's own listings). */}
              {p.org_name && (
                <div style={{ color: h.ac, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>
                  {(t.listedBy || "Listed by")} {p.org_name}
                </div>
              )}
              <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>{formatPropAddr(p)}</div>
              {/* Distance pill — appears only when the user has set a
                  location AND the property has coords. p._distance_km
                  is set upstream in the geoProps useMemo when
                  locFilter.userLocation is active. */}
              {p._distance_km != null && Number.isFinite(p._distance_km) && (
                <div style={{
                  marginTop: 4,
                  display: "inline-flex", alignItems: "center", gap: 3,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: `${h.ac}10`,
                  color: h.ac,
                  fontSize: 10, fontWeight: 600,
                }}>
                  <span style={{ fontSize: 10 }}>{"📍"}</span>
                  {formatDistance(p._distance_km)} {t.away || "away"}
                </div>
              )}
              <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap", marginTop: 2 }}>
                {p.property_type && p.property_type !== "apartment" && <span style={{ display: "inline-block", background: isCommercial(p.property_type) ? `${h.gn}15` : `${h.ac}15`, color: isCommercial(p.property_type) ? h.gn : h.ac, padding: "1px 7px", borderRadius: 8, fontSize: 9, fontWeight: 600 }}>{ptLabel(p.property_type, t)}</span>}
                {role !== "landlord" && <FitBadge fit={getPropertyFit(p, { leases, role, t, h })} h={h} />}
              </div>
              <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{p.rooms} {t.rooms} · ₹{p.rent?.toLocaleString()}/mo</div>
              {/* 2-line teaser of the landlord's description; full text
                  lives on the detail view */}
              {p.description && <div style={{ color: h.ts || h.sc, fontSize: 11, marginTop: 4, lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.description}</div>}
            </div>
            <span style={{ padding: "3px 10px", borderRadius: 10, background: p.occ ? `${h.gn}15` : `${h.wn}15`, color: p.occ ? h.gn : h.wn, fontSize: 10, fontWeight: 600, flexShrink: 0 }}>{p.occ ? t.occupied : t.vacant}</span>
          </div>
          {isBrowseRole && <div style={{ display: "flex", gap: 8, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${h.bd}` }}>
            <button onClick={(e) => { e.stopPropagation(); setDetailProp(p); }}
              style={{ flex: 1, background: h.ac, color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              {t.viewDetails || "View Details"}
            </button>
            {/* "Inquire Now" used to be a near-duplicate of "View
                Details" (both opened the same detail page). Now it
                also scrolls the detail page to the inquiry form once
                it mounts — gives the two buttons distinct purposes.
                Delay is two animation frames so the detail view's
                outer scroll container exists before scrollIntoView
                fires. */}
            {!p.occ && <button onClick={(e) => {
              e.stopPropagation();
              setDetailProp(p);
              setInqSent(false);
              requestAnimationFrame(() => requestAnimationFrame(() => {
                const el = document.getElementById('inquiry-form');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }));
            }}
              style={{ flex: 1, background: `${h.gn}15`, color: h.gn, border: `1px solid ${h.gn}30`, padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              {t.inquireNow || "Inquire Now"}
            </button>}
          </div>}
          </div>
        </div>; })}
      </div>
    )}

    {/* Property Detail + Inquiry Overlay */}
    {detailProp && <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, width: "100%", background: h.bg, zIndex: 9999, overflowY: "auto" }}>
      <div style={{ maxWidth: 430, margin: "0 auto", padding: 16, paddingBottom: 80 }}>
        {/* Back button */}
        <button onClick={() => { setDetailProp(null); setInqSent(false); setInqName(""); setInqPhone(""); setInqMsg(""); }}
          style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
          <Ic n="arrow-left" s={14} c={h.ac} /> {t.back || "Back"}
        </button>

        {/* Fullscreen Photo Viewer */}
        {fullscreen && (detailProp.photos || []).length > 0 && <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.95)", zIndex: 99999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <button onClick={() => setFullscreen(false)} style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", width: 36, height: 36, borderRadius: "50%", fontSize: 18, cursor: "pointer", zIndex: 2 }}>{"\u2715"}</button>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{photoIdx + 1} / {detailProp.photos.length}</div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%", position: "relative" }}>
            {detailProp.photos.length > 1 && <button onClick={() => setPhotoIdx(i => i > 0 ? i - 1 : detailProp.photos.length - 1)} style={{ position: "absolute", left: 8, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", width: 40, height: 40, borderRadius: "50%", fontSize: 20, cursor: "pointer", zIndex: 2 }}>{"\u2039"}</button>}
            <img src={detailProp.photos[photoIdx]} alt="" style={{ maxWidth: "90%", maxHeight: "80vh", objectFit: "contain", borderRadius: 8 }} />
            {detailProp.photos.length > 1 && <button onClick={() => setPhotoIdx(i => i < detailProp.photos.length - 1 ? i + 1 : 0)} style={{ position: "absolute", right: 8, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", width: 40, height: 40, borderRadius: "50%", fontSize: 20, cursor: "pointer", zIndex: 2 }}>{"\u203A"}</button>}
          </div>
          <div style={{ display: "flex", gap: 6, padding: 16, overflowX: "auto", maxWidth: "100%" }}>
            {detailProp.photos.map((url, i) => <img key={i} src={url} alt="" onClick={() => setPhotoIdx(i)} style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6, border: `2px solid ${photoIdx === i ? "#fff" : "rgba(255,255,255,0.2)"}`, cursor: "pointer", flexShrink: 0 }} />)}
          </div>
        </div>}

        {/* Photos */}
        {(() => { const dpPhotos = detailProp.photos || []; return dpPhotos.length > 0 ? (
          <div style={{ borderRadius: rad.md, overflow: "hidden", marginBottom: 12 }}>
            <div onClick={() => { setPhotoIdx(0); setFullscreen(true); }} style={{ position: "relative", cursor: "pointer" }}>
              <img src={dpPhotos[photoIdx]} alt="" style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }} />
              <div style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(0,0,0,0.6)", color: "#fff", padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{photoIdx + 1}/{dpPhotos.length} {"\uD83D\uDD0D"}</div>
            </div>
            {dpPhotos.length > 1 && <div style={{ display: "flex", gap: 6, padding: 8, overflowX: "auto" }}>
              {dpPhotos.map((url, i) => <img key={i} src={url} alt="" onClick={() => setPhotoIdx(i)} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: `3px solid ${photoIdx === i ? h.ac : h.bd}`, cursor: "pointer", flexShrink: 0, transition: "border-color 0.2s" }} />)}
            </div>}
          </div>
        ) : (
          <div style={{ height: 140, borderRadius: rad.md, background: h.ib, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12, border: `1px solid ${h.bd}` }}>
            <Ic n="home" s={40} c={h.tt} />
          </div>
        ); })()}

        {/* Property Info */}
        <div style={{ ...crd(h) }}>
          {/* "Listed by <Org>" trust label — same component used on
              PublicBrowsePage and the map popup card so the tenant
              sees a consistent attribution wherever a property appears. */}
          {detailProp.org_name && (
            <div style={{ color: h.ac, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
              {(t.listedBy || "Listed by")} {detailProp.org_name}
            </div>
          )}
          {Number(detailProp.ll_count) > 0 && (
            <div style={{ marginBottom: 8 }}>
              <RatingSummary avg={Number(detailProp.ll_avg) || 0} count={Number(detailProp.ll_count) || 0} h={h} size={13} />
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <h2 style={{ color: h.tx, fontSize: 16, fontWeight: 700, margin: 0 }}>{formatPropAddr(detailProp)}</h2>
            <span style={bdg(!detailProp.occ ? `${h.gn}15` : `${h.rd}15`, !detailProp.occ ? h.gn : h.rd)}>
              {detailProp.occ ? t.occupied || "Occupied" : t.vacant || "Vacant"}
            </span>
          </div>

          <div style={{ fontSize: 22, fontWeight: 800, color: h.ac, marginBottom: 4 }}>{"\u20B9"}{detailProp.rent?.toLocaleString()}<span style={{ fontSize: 13, fontWeight: 400, color: h.ts }}>/mo</span></div>
          {detailProp.dep > 0 && <div style={{ fontSize: 12, color: h.ts, marginBottom: 12 }}>{t.deposit || "Deposit"}: {"\u20B9"}{detailProp.dep?.toLocaleString()}</div>}

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            {detailProp.property_type && <span style={bdg(`${h.ac}12`, h.ac)}>{ptLabel(detailProp.property_type, t)}</span>}
            {detailProp.rooms && <span style={bdg(`${h.ac}12`, h.ac)}>{detailProp.rooms} {t.rooms || "Rooms"}</span>}
            {detailProp.area_sqft && <span style={bdg(`${h.ac}12`, h.ac)}>{detailProp.area_sqft} sqft</span>}
            {detailProp.furnishing && detailProp.furnishing !== "unfurnished" && <span style={bdg(`${h.gn}12`, h.gn)}>{detailProp.furnishing}</span>}
            {detailProp.parking && <span style={bdg(`${h.wn}12`, h.wn)}>{"\uD83C\uDD7F\uFE0F"} {detailProp.parking}</span>}
          </div>

          {/* Landlord's marketing description */}
          {detailProp.description && <>
            <div style={{ color: h.ts, fontSize: 11, fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>{t.aboutProperty || "About this property"}</div>
            <div style={{ color: h.tx, fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 12 }}>{detailProp.description}</div>
          </>}

          {/* Amenities */}
          {(detailProp.amen || []).length > 0 && <>
            <div style={{ color: h.ts, fontSize: 11, fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>{t.amenities || "Amenities"}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
              {detailProp.amen.map((a, i) => <span key={i} style={{ padding: "4px 10px", borderRadius: 16, background: h.ib, color: h.tx, fontSize: 11, border: `1px solid ${h.bd}` }}>{a}</span>)}
            </div>
          </>}

          {role !== "landlord" && <FitBadge fit={getPropertyFit(detailProp, { leases, role, t, h })} h={h} />}
        </div>

        {/* Landlord reputation — lets a prospect vet the owner before inquiring */}
        {role !== "landlord" && detailProp.org_id && <LandlordReviews orgId={detailProp.org_id} h={h} t={t} />}

        {/* Inquiry Form — id="inquiry-form" so the listing card's
            "Inquire Now" button can scrollIntoView() it after opening
            the detail page. */}
        {!detailProp.occ && <div id="inquiry-form" style={{ ...crd(h), marginTop: 12 }}>
          {inqSent ? (
            <div style={{ textAlign: "center", padding: sp.lg }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>{"\u2705"}</div>
              <div style={{ color: h.gn, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{t.inquirySent || "Inquiry Sent!"}</div>
              <div style={{ color: h.ts, fontSize: 12 }}>{t.inquiryDesc || "The landlord will contact you soon."}</div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Ic n="send" s={16} c={h.ac} />
                <span style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{t.inquireNow || "Inquire Now"}</span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ color: h.ts, fontSize: 11, display: "block", marginBottom: 4 }}>{t.name || "Your Name"} *</label>
                <input value={inqName} onChange={e => setInqName(e.target.value)} placeholder="Enter your name"
                  style={{ width: "100%", padding: "10px 14px", borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ color: h.ts, fontSize: 11, display: "block", marginBottom: 4 }}>{t.phone || "Your Phone"} *</label>
                <input value={inqPhone} onChange={e => setInqPhone(e.target.value)} placeholder="+91 98765 43210"
                  style={{ width: "100%", padding: "10px 14px", borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ color: h.ts, fontSize: 11, display: "block", marginBottom: 4 }}>{t.message || "Message"}</label>
                <textarea value={inqMsg} onChange={e => setInqMsg(e.target.value)} rows={3}
                  placeholder={`I'm interested in ${formatPropAddr(detailProp)}`}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 14, outline: "none", resize: "none", boxSizing: "border-box" }} />
              </div>
              <button disabled={inqSending || !inqName || !inqPhone} onClick={async () => {
                if (!inqName.trim() || !inqPhone.trim() || inqPhone.replace(/\D/g, "").length < 10) {
                  flash(t.fillRequired || "Name and valid phone required", "error"); return;
                }
                setInqSending(true);
                const res = await api.submitLead({ property_id: detailProp.id, name: inqName.trim(), phone: inqPhone.trim(), message: inqMsg.trim() });
                setInqSending(false);
                if (res.success) {
                  setInqSent(true); flash(t.inquirySent || "Inquiry sent!");
                } else {
                  flash(res.message || "Failed to send inquiry", "error");
                }
              }} style={{ ...btnP(h), opacity: inqSending || !inqName || !inqPhone ? 0.6 : 1 }}>
                {inqSending ? "Sending..." : (t.sendInquiry || "Send Inquiry")}
              </button>
            </>
          )}
        </div>}

        {/* Occupied property message */}
        {detailProp.occ && <div style={{ ...crd(h), textAlign: "center", padding: sp.lg, marginTop: 12 }}>
          <div style={{ color: h.ts, fontSize: 13 }}>{t.propOccupied || "This property is currently occupied."}</div>
        </div>}
      </div>
    </div>}
  </div>;
}

export default MapViewPage;
